import React, { useEffect, useState } from "react";
import API from "../api/axios";
import { useNavigate, useLocation } from "react-router-dom";
import {
  User,
  Mail,
  Lock,
  Phone,
  Briefcase,
  Users,
  Gift,
  Loader2,
  AlertCircle,
  CheckCircle,
  ArrowLeft,
} from "lucide-react";
import logo from '../asset/kestren.png';

const PHONE_REGEX = /^\+?[1-9]\d{6,14}$/;

export default function Register() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    gender: "other",
    profession: "",
    ref: "",
  });

  const [qrFile, setQrFile] = useState(null);
  const [preview, setPreview] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const navigate = useNavigate();
  const location = useLocation();

  // Prefill referral code
  useEffect(() => {
    const code = new URLSearchParams(location.search).get("ref");
    if (code) setForm((f) => ({ ...f, ref: String(code).toUpperCase() }));
  }, [location.search]);

  // QR image upload (Multer compatible)
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setQrFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    if (!PHONE_REGEX.test(form.phone)) {
      setLoading(false);
      setError("Please enter a valid phone number (e.g., +919876543210).");
      return;
    }

    try {
      // Build FormData for Multer
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("password", form.password);
      fd.append("phone", form.phone);
      fd.append("gender", form.gender);
      fd.append("profession", form.profession);
      fd.append("ref", form.ref.trim().toUpperCase());

      if (qrFile) {
        fd.append("qrImage", qrFile);
      }

      await API.post("/auth/register", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setSuccess("Account created successfully! Redirecting...");
      setTimeout(() => navigate("/login"), 2000);

    } catch (err) {
      setError(
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Registration failed. Try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 p-10">

          {/* Logo */}
          <div className="text-center mb-10 px-4">
            <div className="flex justify-center mb-6">
              <img src={logo} className="w-32 h-32 rounded-3xl shadow-xl border-4 border-gray-200" alt="Logo" />
            </div>

            <h1 className="text-4xl font-bold text-gray-800">Client Registration</h1>
            <p className="text-gray-600 mt-3 text-lg">Create your wallet account in seconds</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">

            {/* Name */}
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Full Name"
                className="w-full pl-14 py-4 bg-gray-50 border rounded-2xl"
                required
              />
            </div>

            {/* Email */}
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Enter email"
                className="w-full pl-14 py-4 bg-gray-50 border rounded-2xl"
                required
              />
            </div>

            {/* Phone */}
            <div className="relative">
              <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="Phone (+919876543210)"
                className="w-full pl-14 py-4 bg-gray-50 border rounded-2xl"
                required
              />
            </div>

            {/* Gender */}
            <div className="relative">
              <Users className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
              <select
                value={form.gender}
                onChange={(e) => setForm({ ...form, gender: e.target.value })}
                className="w-full pl-14 py-4 bg-gray-50 border rounded-2xl"
              >
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Profession */}
            <div className="relative">
              <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                value={form.profession}
                onChange={(e) => setForm({ ...form, profession: e.target.value })}
                placeholder="Profession (optional)"
                className="w-full pl-14 py-4 bg-gray-50 border rounded-2xl"
              />
            </div>

            {/* Referral */}
            <div className="relative">
              <Gift className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                value={form.ref}
                onChange={(e) => setForm({ ...form, ref: e.target.value.toUpperCase().replace(/\s+/g, "") })}
                placeholder="Referral Code (optional)"
                className="w-full pl-14 py-4 bg-gray-50 border rounded-2xl uppercase"
              />
            </div>

            {/* Password */}
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Create password"
                className="w-full pl-14 py-4 bg-gray-50 border rounded-2xl"
                required
              />
            </div>

            {/* QR Upload */}
            <div>
              <label className="font-medium text-gray-700 pl-2">QR Code</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                required
                className="w-full mt-2 py-4 px-4 bg-gray-50 border rounded-2xl"
              />

              {preview && (
                <div className="mt-4 flex justify-center">
                  <img src={preview} className="w-40 h-40 object-cover rounded-2xl border shadow-md" />
                </div>
              )}
            </div>

            {/* Error */}
            {error && (
              <div className="p-5 bg-red-50 border border-red-200 text-red-700 rounded-2xl">
                <p>{error}</p>
              </div>
            )}

            {/* Success */}
            {success && (
              <div className="p-5 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-2xl">
                <p>{success}</p>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className={`w-full py-5 rounded-2xl font-bold text-white flex justify-center gap-3 
              ${loading ? "bg-gray-500" : "bg-gray-800 hover:bg-gray-900"}`}
            >
              {loading ? <Loader2 className="animate-spin" /> : "Create Account"}
            </button>
          </form>

          <div className="mt-8 text-center">
            <button onClick={() => navigate("/login")} className="text-gray-700 hover:text-gray-900">
              <ArrowLeft className="inline w-5 h-5 mr-2" /> Back to Login
            </button>
          </div>
        </div>

        <p className="text-center text-xs text-gray-500 mt-10">© {new Date().getFullYear()} KESTREN</p>
      </div>
    </div>
  );
}
